import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { routes } from '@platform/core/lib/routes';
import { TextCommit } from '@platform/features/workspaces/builder';
import { tileNav } from '@platform/navigation';
import { useUpdateCustomModuleMutation, useUpdateWorkspaceModuleMutation } from '@platform/state/api';
import { ModuleTile } from '@platform/ui';
import { useWorkspaceMeta } from '../../shell/useWorkspaceMeta';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { useCustomizationScope } from '../system/useCustomizationScope';
import { useFailToast } from '../system/useFailToast';
import { canResetRow } from './canResetRow';
import { ModuleSwitchCell } from './ModuleSwitchCell';
import { moduleRowStates } from './moduleRowStates';
import { typeRows } from './typeRows';
import { typeRowText } from './typeRowText';
import { useModuleCategory } from './useModuleCategory';
import { useModuleReset } from './useModuleReset';
import { useRenameRecordType } from './useRenameRecordType';
import { useWorkspaceDefinitions } from './useWorkspaceDefinitions';
import './customize.css';

const COLUMNS = 'minmax(200px, 1.8fr) minmax(110px, 1fr) minmax(150px, 1.2fr) minmax(130px, 1fr) 160px 230px';

/**
 * «Встановлені модулі» (прототип v4 G:L8442–8530, pgCustMods; рівень «Глобально» — GlobalCatalogPage): рядок — кожен тип запису простору в
 * порядку прототипу; плитка — таблиці (власна іконка дочірньої таблиці прототипу); назва редагується на місці (головний тип — назва модуля
 * в просторі, PATCH модуля; дочірній — назва типу, перевизначення ADR-023), «Увімкнено» (ModuleSwitchCell: вимкнений модуль зникає з меню,
 * записи зберігаються; вимкнений глобально — «Вимкнено глобально», простір його не вмикає), «Відкрити» (повернення до модуля без скидання
 * подання, прототип onOpen — page:'module'), «Записи й форми» (у макеті «Налаштування»: «… › Модулі › Справи», «← Модулі»), «Скинути»
 * (рішення 2 етапу 9: перейменування, вимкнення чи перевизначення типів модуля — після підтвердження). Рядки 45px (відступ 7px 14px).
 */
export function CustModulesPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const { ws } = useWorkspaceParams();
  const { workspace } = useWorkspaceMeta(ws);
  const scope = useCustomizationScope(ws);
  const { catalog, definitions } = useWorkspaceDefinitions(ws, false);
  const [update] = useUpdateWorkspaceModuleMutation();
  const [updateCustom] = useUpdateCustomModuleMutation();
  const rename = useRenameRecordType(ws);
  const reset = useModuleReset(ws);
  const category = useModuleCategory(catalog);
  const fail = useFailToast();
  const canEdit = scope.canWs;
  const states = moduleRowStates(typeRows(definitions ?? []), catalog, label);
  const rows = states.map((s) => {
    const key = s.row.def.module.key;
    const nav = tileNav(key, s.row.rt.key);
    const types = typeRowText(s.row, label, t);
    const forms = typeRowText(s.row, label, t, true);
    const patch = (body: { enabled?: boolean; name?: string }) => update({ ws, moduleKey: key, body }).unwrap().catch(fail);
    // власний модуль (ADR-025) перейменовує своє визначення; модуль каталогу — назва в просторі (головна таблиця) чи перевизначення (дочірня)
    const commitName = (value: string) => (s.row.def.module.kind === 'custom'
      ? (value.trim() ? updateCustom({ ws, key, body: { name: value.trim() } }).unwrap().catch(fail) : undefined)
      : s.row.rt.main ? patch({ name: value.trim() }) : rename(key, s.row.rt.key, value, s.published).catch(fail));
    const hostName = s.row.rt.main ? undefined : label(catalog?.modules.find((m) => m.key === key)?.name) || key;
    return {
      key: `${key}/${s.row.rt.key}`,
      cells: [
        <span key="n" className="sp-who"><ModuleTile icon={nav.icon} tone={nav.tone} size={28} />
          <TextCommit className="cu-modname" value={s.name} label={t('sysCust.modules.name')} title={t('sysCust.modules.rename')} disabled={!canEdit} onCommit={commitName} /></span>,
        <span key="c" className="sp-muted">{category(key, s.row.def.module.category)}</span>,
        <span key="t" title={types.tip}>{types.text}</span>,
        <span key="f" title={forms.tip}>{forms.text}</span>,
        <ModuleSwitchCell key="s" enabled={s.enabled} canEdit={canEdit && !s.globalOff} required={s.required} hostName={hostName}
          offNote={s.globalOff ? t('sysCust.modules.offGlobal') : undefined} onToggle={(on) => void patch({ enabled: on })} />,
        <span key="a" className="sp-actions">
          {s.enabled && <Link className="sp-btn" to={s.row.rt.main ? routes.moduleReturn(key) : routes.list(key, s.row.rt.key)}>{t('sysCust.modules.open')}</Link>}
          <Link className="sp-btn sp-btn--accent" to={routes.customizeModule(key, s.row.rt.main ? undefined : s.row.rt.key)}>{t('sysCust.modules.recordsForms')}</Link>
          {canEdit && canResetRow(s) && (
            <button type="button" className="sp-btn sp-btn--danger" title={t('sysCust.modules.resetTip')} aria-label={t('sysCust.modules.resetTip')}
              onClick={() => reset(s)}>{t('sysCust.modules.reset')}</button>
          )}
        </span>,
      ],
    };
  });
  const head = [t('sysCust.modules.module'), t('sysCust.modules.category'), t('sysCust.modules.types'), t('sysCust.modules.forms'), t('sysCust.modules.enabled'), ''];
  return (
    <SystemPage title={t('sysPages.sections.installed')} sub={t('sysCust.modules.subWs', { ws: workspace?.name ?? '' })}
      actions={<Link className="sp-btn sp-btn--primary" to={routes.modules()}>{t('sysCust.modules.add')}</Link>}>
      <div className="cu-modrows"><SysTable columns={COLUMNS} minWidth={1000} head={head} rows={rows} label={t('sysPages.sections.installed')} /></div>
    </SystemPage>
  );
}
